import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { message, Modal } from "antd";
import { Globe2, Loader2, Play } from "lucide-react";
import { getToken, hasRole } from "../api/auth";
import { requestJson } from "../api/httpClient";
import styles from "./ChipDipCollection.module.css";

const CollectionContext = createContext(null);
const activeJob = (job) => job && ["QUEUED", "PROCESSING"].includes(job.status);
const terminal = (job) => job && !activeJob(job);
const orderIdOf = (job) => job.order_id ?? job.orderId;
const parserIdOf = (job) => job.parser_id ?? job.parserId;
const keyOf = (job) => `${orderIdOf(job)}:${parserIdOf(job)}`;
const parserName = (job) => job.parser_name ?? job.parserName ?? "Сайт";

// [[ChipDip Order Collection]] #obsidian-marker
export function ChipDipCollectionProvider({ children, onCompleted }) {
  const [vpn, setVpn] = useState({ available: false, state: "CHECKING" });
  const [jobs, setJobs] = useState({});
  const [sources, setSources] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pending, setPending] = useState({});
  const jobsRef = useRef({});
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    let active = true;
    let socket;
    let reconnect;
    let fetching = false;
    let refreshAgain = false;
    let initialized = false;
    const refresh = async () => {
      if (fetching) { refreshAgain = true; return; }
      fetching = true;
      try {
        const [data, available] = await Promise.all([
          requestJson("/api/site-collection/jobs"), requestJson("/api/site-parsers")
        ]);
        if (!active) return;
        setSources(available);
        const next = { ...jobsRef.current };
        let completed = false;
        for (let job of data) {
          const id = keyOf(job);
          const previous = jobsRef.current[id];
          if (previous && (previous.id > job.id || (previous.id === job.id &&
              (previous.processed > job.processed || (terminal(previous) && !terminal(job)))))) job = previous;
          next[id] = job;
          if (initialized && terminal(job) && (previous?.id !== job.id || previous?.status !== job.status)) {
            completed = true;
            if (job.status === "COMPLETED") message.success(`${parserName(job)}: заказ ${orderIdOf(job)}, найдено ${job.found}, не найдено ${job.skipped}`);
            if (job.status === "PARTIAL") message.warning(`${parserName(job)}: заказ ${orderIdOf(job)}. Частично сохранено, найдено ${job.found}. ${job.error || ""}`);
            if (job.status === "EMPTY") message.info(`${parserName(job)}: для заказа ${orderIdOf(job)} позиции не найдены`);
            if (job.status === "FAILED") message.error(`${parserName(job)}: заказ ${orderIdOf(job)}. ${job.error || "Ошибка обработки"}`);
          }
        }
        initialized = true;
        jobsRef.current = next;
        setJobs(next);
        setLoaded(true);
        setLoadError(false);
        if (completed) {
          Promise.resolve(onCompletedRef.current?.()).catch(() => message.error("Не удалось обновить список предложений"));
          window.dispatchEvent(new Event("chipdip-completed"));
        }
      } catch {
        // Keep the last known processing state; the next poll/reconnect retries.
        if (active) setLoadError(true);
      } finally {
        fetching = false;
        if (active && refreshAgain) { refreshAgain = false; refresh(); }
      }
    };
    const connect = () => {
      if (!active || !getToken()) return;
      const configured = process.env.REACT_APP_CHIPDIP_WS_URL;
      const url = configured || `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws/chipdip`;
      try {
        socket = new WebSocket(url);
        socket.onopen = () => socket.send(JSON.stringify({ type: "authenticate", token: getToken() }));
        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "ready" || data.type === "chipdip.updated") refresh();
          } catch { /* Ignore malformed events; REST remains the source of truth. */ }
        };
        socket.onclose = () => { if (active) reconnect = setTimeout(connect, 5000); };
        socket.onerror = () => socket.close();
      } catch {
        reconnect = setTimeout(connect, 5000);
      }
    };
    refresh();
    connect();
    const poll = setInterval(refresh, 15000);
    const ping = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "ping" }));
    }, 25000);
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      clearTimeout(reconnect);
      clearInterval(poll);
      clearInterval(ping);
      window.removeEventListener("focus", refresh);
      if (socket) { socket.onclose = null; socket.close(); }
    };
  }, []);

  // [[VPN Connectivity]] #obsidian-marker
  useEffect(() => {
    let active=true, fetching=false, controller;
    const refresh=async()=>{
      if(fetching)return;fetching=true;
      controller=new AbortController();const timeout=setTimeout(()=>controller.abort(),5000);
      try{const status=await requestJson("/api/site-parsers/vpn", {signal:controller.signal});if(active)setVpn(status || {available:false,state:"UNKNOWN"});}
      catch{if(active)setVpn({available:false,state:"UNKNOWN"});}
      finally{clearTimeout(timeout);fetching=false;}
    };
    refresh();const timer=setInterval(refresh,15000);
    window.addEventListener("focus",refresh);
    return()=>{active=false;controller?.abort();clearInterval(timer);window.removeEventListener("focus",refresh);};
  }, []);

  const start = useCallback(async (orderId, parserId, selectionMode) => {
    const key = `${orderId}:${parserId}`;
    setPending((current) => ({ ...current, [key]: true }));
    try {
      const job = await requestJson(`/api/orders/${orderId}/site-parsers/${parserId}/collect?selection_mode=${selectionMode}`, { method: "POST" });
      // A socket refresh may already have received a later state of this job.
      const known = jobsRef.current[key];
      if (!known || known.id < job.id || (known.id === job.id && !terminal(known) && known.processed <= job.processed)) {
        jobsRef.current = { ...jobsRef.current, [key]: job };
        setJobs(jobsRef.current);
      }
    } catch (error) {
      let detail = error.message;
      try {
        const body = JSON.parse(detail);
        detail = body.message || detail;
        if (body.code === "VPN_UNAVAILABLE") setVpn({ available: false, state: "UNAVAILABLE" });
      } catch { /* Non-JSON transport errors already contain a readable message. */ }
      message.error(`Не удалось запустить сбор: ${detail}`);
    } finally {
      setPending((current) => ({ ...current, [key]: false }));
    }
  }, []);

  return <CollectionContext.Provider value={{ jobs, sources, pending, loaded, loadError, start, vpn }}>{children}</CollectionContext.Provider>;
}

export default function ChipDipCollection({ orderId }) {
  const context = useContext(CollectionContext);
  const [open, setOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState("NEAREST_QUANTITY");
  if (!context) return null;
  const { jobs, sources, pending, loaded, loadError, start, vpn } = context;
  const orderJobs = Object.values(jobs).filter((job) => String(orderIdOf(job)) === String(orderId));
  const processing = orderJobs.some(activeJob) ||
    Object.keys(pending).some((key) => key.startsWith(`${orderId}:`) && pending[key]);
  const visibleSources = sources.filter((source) => source.enabled !== false && !source.disabled);
  const vpnState = vpn.available ? "available" : vpn.state === "UNAVAILABLE" ? "unavailable" : "checking";
  const vpnLabel = vpnState === "available" ? "VPN доступен" : vpnState === "unavailable" ? "VPN недоступен" : "VPN проверяется / не подтверждён";
  const content = (
    <div className={styles.menu} onClick={(event) => event.stopPropagation()}>
      <label className={styles.selectionMode}>
        Режим получения предложений
        <select value={selectionMode} disabled={processing} onChange={(event) => setSelectionMode(event.target.value)}>
          <option value="ALL">Все предложения</option>
          <option value="NEAREST_QUANTITY">Подобрать по количеству заказа</option>
        </select>
      </label>
      {selectionMode === "NEAREST_QUANTITY" && <p className={styles.modeHint}>
        Одна строка на позицию: ближайшее количество не меньше заказа, иначе — ближайшее меньшее.
        Если количество не определено, сохраним первую найденную строку.
      </p>}
      {loadError && <p className={styles.error}>Связь с сервером потеряна. Повтор через 15 с.</p>}
      {!loaded && <p>Получение статуса…</p>}
      {loaded && !visibleSources.length && <p>Нет включённых сайтов. Обратитесь к администратору.</p>}
      <div className={styles.sourceList} role="region" aria-label="Список парсеров" tabIndex={0}>
      {visibleSources.map((source) => {
        const key = `${orderId}:${source.id}`;
        const job = jobs[key];
        const busy = pending[key] || activeJob(job);
        const needsVpn = /^(mouser|digikey)/i.test(source.code || "");
        const vpnBlocked = needsVpn && !vpn.available;
        return (
          <div className={styles.source} key={source.id}>
            <div>
              <b>{source.name}</b>
              <span role="status" className={job?.status === "FAILED" ? styles.error : undefined}>
                {busy ? `${(job?.remote_status ?? job?.remoteStatus ?? job?.status) === "QUEUED" ? "В очереди" : "В обработке"}: ${job?.processed || 0} / ${job?.total || "…"}`
                  : job?.status === "PARTIAL" ? `Частично сохранено. Обработано: ${job.processed}/${job.total}, найдено: ${job.found}. ${job.error || ""}`
                  : job?.status === "COMPLETED" ? `Найдено: ${job.found}, не найдено: ${job.skipped}`
                  : job?.status === "EMPTY" ? `Ничего не найдено; не найдено: ${job.skipped}`
                  : job?.status === "FAILED" ? job.error || "Ошибка обработки" : "Ещё не запускался"}
              </span>
              {vpnBlocked && <small className={styles.error}>Ожидание VPN</small>}
              {busy && job?.error && <small className={styles.error}>{job.error}</small>}
              {busy && source.code === "mouser" && <small>Выполняется поиск Mouser. Результат обновится автоматически.</small>}
            </div>
            <button type="button" disabled={!loaded || loadError || busy || vpnBlocked}
              title={vpnBlocked ? "Для этого источника требуется VPN" : `Получить данные: ${source.name}`} aria-label={`Получить данные: ${source.name}`}
              onClick={() => start(orderId, source.id, selectionMode)}>
              {busy ? <Loader2 size={16} className={styles.spin} /> : <Play size={16} />}
            </button>
          </div>
        );
      })}
      </div>
      {hasRole("ADMIN") && <a href="/admin?tab=parsers">Настроить сайты в администрировании</a>}
    </div>
  );
  return (
    <>
      <button type="button" className={styles.iconButton}
        aria-label="Получить данные с сайтов" title={processing ? "Сбор данных: в обработке" : "Получить данные с сайтов"}
        onClick={(event) => { event.stopPropagation(); setOpen(true); }}>
        {processing ? <Loader2 size={16} className={styles.spin} /> : <Globe2 size={16} />}
      </button>
      <Modal open={open} onCancel={() => setOpen(false)} centered width={920} footer={null}
        className={styles.modal} destroyOnHidden
        title={<div className={styles.header}>
          <span>Получить данные с сайтов</span>
          <span role="status" aria-label={vpnLabel} className={`${styles.vpnBadge} ${styles[vpnState]}`}>
            <span className={styles.vpnDot} aria-hidden="true" />{vpnLabel}
          </span>
          <small className={styles.vpnHint}>Mouser и DigiKey работают только через VPN. Проверка каждые 15 секунд.</small>
        </div>}>
        {content}
      </Modal>
    </>
  );
}
